import * as React from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import Stats from 'stats.js';
import OrbitControls from 'three-orbitcontrols';
import Hammer from 'hammerjs';

import RoutingService from '../services/Routing';
import SVGService from '../services/SVG';
import SVGToMesh from '../utilities/SVGToMesh';

import RoutingPopup from '../components/RoutingPopup';

import Position from '../classes/Position';
import Waypoint from '../classes/Waypoint';

/**
 * A React component for loading and displaying the provided map file name in a 3D extrusion render
 * 
 * @class ThreeMap
 * @extends {React.Component}
 */
class ThreeMap extends React.Component {
    constructor(props) {
        super(props);

        // Set up ref for injecting Three
        this.mount = React.createRef();
        this.previewMount = React.createRef();

        // Set up state
        this.state = {
            customRouting: null
        };

        // Set up class variables
        this.svgData = null;
        this.renderer = null; 
        this.previewRenderer = null; 
        this.stats = null;
        this.scene = null;
        this.camera = null;
        this.previewCamera = null;
        this.controls = null;
        this.mapGroup = null;
        this.raycaster = null;
        this.origin = null;
        this.route = null;
        this.binormal = new THREE.Vector3();
	    this.normal = new THREE.Vector3();

        // Initialize SVGToMesh
        this.SVGToMesh = SVGToMesh( THREE );

        // Bind callbacks
        this.start = this.start.bind(this);
        this.stop = this.stop.bind(this);
        this.animate = this.animate.bind(this);
        this.onWindowResize = this.onWindowResize.bind(this);
        this.handleDoubletap = this.handleDoubletap.bind(this);
    }

    render () {
        return (
            <React.Fragment>
                {/* TODO: Move this to a component */}
                <div ref={this.mount}
                    className="full-width-height"
                />
                <div ref={this.previewMount}
                    style={{
                        width: "50vw",
                        height: "25vh",
                        position: "fixed",
                        right: 0,
                        bottom: 0,
                        pointerEvents: "none",
                    }}
                />
                {
                    this.state.customRouting
                        ? <RoutingPopup 
                            setOrigin={this.props.setOrigin}
                            setDestination={this.props.setDestination}
                            top={this.state.customRouting.top}
                            nearestWaypoint={this.state.customRouting.nearestWaypoint}
                            clear={() => this.setState({customRouting: null})}
                            />
                        : null
                }
            </React.Fragment>
        );
    }

    /**
     * Kicks of all initializations after the mount point has rendered
     * 
     * @memberof ThreeMap
     */
    componentDidMount () {
        this.initializeScene();
        this.initializeMap();
        this.initializeTap();
        this.initializeCurrentPositionDisplay();
        this.initializeRouteDisplay();
    }

    /**
     * Handles any updates to data and kicks of initializations as needed
     * 
     * @param {any} prevProps Previous set of props, used to compare to this.props
     * @memberof ThreeMap
     */
    componentDidUpdate(prevProps) {
        if (prevProps.mapName !== this.props.mapName) {
            this.initializeMap();
            this.initializeCurrentPositionDisplay();
            // Ensure previous preview is not displayed
            this.destroyPreview();
            this.initializeRouteDisplay();
        }

        // If the origin changed, make sure the current position updates
        if (prevProps.origin !== this.props.origin) {
            this.initializeCurrentPositionDisplay();
        }

        // If the route data changed, update the route display
        if (prevProps.destination !== this.props.destination || 
            prevProps.origin !== this.props.origin) {
            this.initializeRouteDisplay();
        }
    }

    /**
     * Performs the ThreeJS and WebGL setup after the component has mounted
     * 
     * @memberof ThreeMap
     */
    initializeScene () {
        const width = this.mount.current.clientWidth;
        const height = this.mount.current.clientHeight;

        // Setup scene
        this.scene = new THREE.Scene();

        this.camera = new THREE.OrthographicCamera(
            width / - 2,
            width / 2,
            height / 2,
            height / - 2,
            1,
            10000
        );

        this.camera.position.set( 0, 0, 2000 );

        // Setup renderer
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.renderer.setClearColor( 0x000000, 0 );
        this.renderer.setPixelRatio( window.devicePixelRatio );
        this.renderer.setSize(width, height);

        // Setup controls
        this.controls = new OrbitControls( this.camera, this.renderer.domElement );
        this.controls.enableDamping = false;
        this.controls.minAzimuthAngle = - Math.PI / 2; // radians
        this.controls.maxAzimuthAngle = Math.PI / 2; // radians

        // Setup stats
        this.stats = new Stats();
        this.stats.showPanel( 0 );
        // Move stats to the bottom
        this.stats.domElement.style.top = "auto";
        this.stats.domElement.style.bottom = "25vw";
        this.stats.domElement.style.left = "auto";
        this.stats.domElement.style.right = "0px";
        this.stats.domElement.style.opacity = "0.5";
        this.mount.current.appendChild( this.stats.dom );

        // Add lights to scene
        var directionalLight = new THREE.DirectionalLight( 0xffffff, 0.6 );
        directionalLight.position.set( 0.75, 0.75, 1.0 ).normalize();
        this.scene.add( directionalLight );
        var ambientLight = new THREE.AmbientLight( 0xcccccc, 0.2 );
        this.scene.add( ambientLight );

        // // Add grid helper
        // var helper = new THREE.GridHelper( 2000, 10 );
        // helper.rotation.x = Math.PI / 2;
        // this.scene.add( helper );

        // // // Add axes helper
        // var axesHelper = new THREE.AxesHelper( 500 );
        // this.scene.add( axesHelper );

        // Set up raycaster
        this.raycaster = new THREE.Raycaster();

        // Initialize the renderer and start render loop
        this.mount.current.appendChild(this.renderer.domElement);
        this.start();

        // Listen for window resize events
        window.addEventListener( 'resize', this.onWindowResize, false );
    }

    /**
     * Performs all operations specific to and dependent on the SVG map data
     * 
     * @memberof ThreeMap
     */
    initializeMap () {
        if (this.mapGroup !== null) {
            this.scene.remove(this.mapGroup);
        }

        this.mapGroup = new THREE.Group();
        this.scene.add( this.mapGroup );

        this.svgData = SVGService.getSVGDataByName( this.props.mapName );

        // Setup SVG geometry
        this.parseSVGIntoGroup(
            this.svgData,
            this.mapGroup
        );

        // Get viewbox data to offset camera/control position from 0,0,0 to center of map
        const viewBox = this.svgData.getAttribute("viewBox").split(" "),
            boxWidth = viewBox[2],
            boxHeight = viewBox[3],
            // FIXME: These values are funky... don't trust them.
            offsetX = boxWidth / 2,
            offsetY = boxHeight / - 4;

        this.camera.position.setX( offsetX );
        this.camera.position.setY( offsetY );

        this.controls.target = new THREE.Vector3(
            offsetX,
            offsetY,
            0
        );
        this.controls.update();

        // Rotate map to align with camera position
        this.mapGroup.rotation.x = 180 * Math.PI / 180;
    }

    /**
     * Sets up the tap recognizer, using Hammer.js
     * 
     * @memberof ThreeMap
     */
    initializeTap () {
        const mc = new Hammer.Manager(this.mount.current);

        // Tap recognizer with minimal 2 taps
        mc.add( new Hammer.Tap({ event: 'doubletap', taps: 2 }) );

        mc.on("doubletap", (ev) => {
            this.handleDoubletap(ev);
        });
    }

    /**
     * Implements the display of the current position indicator
     * 
     * @memberof ThreeMap
     */
    initializeCurrentPositionDisplay () {
        const { mapName, origin } = this.props;
        
        // Remove old origin, if exists
        if (this.origin) {
            this.scene.remove( this.origin );
            this.origin = null;
        }

        // Check if we have an origin set, render it if so
        if (origin && origin.map === mapName) {
            const geometry = new    THREE.TorusGeometry( 20, 5, 16, 10 );

            const material = new THREE.MeshBasicMaterial( { color: 0x7f369b } );
            const lineMaterial = new THREE.LineBasicMaterial( { color : 0xe6c6f3 } );

            this.origin = new THREE.Mesh( geometry, material );
            // Add lines
            this.origin.add( new THREE.Line( geometry, lineMaterial ));

            this.origin.position.set(
                origin.position.X,
                - origin.position.Y,
                // TODO: Resolve discrepency between waypoint Z and map Z
                0 // origin.position.Z
            );
            this.scene.add( this.origin );
        }
    }

    /**
     * Implements the display of the route
     * 
     * @returns 
     * @memberof ThreeMap
     */
    initializeRouteDisplay () {
        const { origin, destination, mapName } = this.props;

        if (this.route) {
            this.scene.remove(this.route);
            this.route = null;
        }

        // Check if we have a set route start/end for route display
        if ( origin && destination ) {
            const route = RoutingService.GetRoute(origin, destination);
            if (route !== null) {
                if (!route.maps.includes(mapName)) {
                    // Ensure no preview is displayed
                    this.destroyPreview();
                    return;
                }

                const curve = new THREE.CatmullRomCurve3(
                    route.route
                        // Ensure nodes are on the currently displayed map
                        .filter((node) => node.waypoint.location.indexOf(this.props.mapName) >= 0)
                        .map((node) => 
                            new THREE.Vector3(
                                node.waypoint.position.X,
                                - node.waypoint.position.Y,
                                // TODO: Resolve discrepency between waypoint Z and map Z
                                0 //node.waypoint.position.Z,
                            )
                        )
                );

                const geometry = new THREE.TubeBufferGeometry( curve, 100, 5, 16, false );

                const material = new THREE.LineBasicMaterial( { color : 0x7f369b } );
                const lineMaterial = new THREE.LineBasicMaterial( { color : 0xe6c6f3 } );

                // Create the final object to add to the scene
                this.route = new THREE.Mesh( geometry, material );
                this.route.add( new THREE.Line( geometry, lineMaterial ));

                this.scene.add(this.route);

                this.initializePreview();
            } else {
                // If route is null, display an error and clear the destination choice
                this.props.setError("No route is available from your current location.");
                this.props.setDestination(null);
            }
        } else {
            // Ensure no preview is displayed
            this.destroyPreview();
        }
    }

    /**
     * Implements the display of a second renderer for the first-person route preview
     * 
     * @memberof ThreeMap
     */
    initializePreview () {
        // Tear down previous preview
        this.destroyPreview();

        const width = this.previewMount.current.clientWidth;
        const height = this.previewMount.current.clientHeight;

        // Setup camera
        this.previewCamera = new THREE.PerspectiveCamera( 84, width / height, 0.01, 1000 );
        this.scene.add(this.previewCamera);

        // Setup renderer
        this.previewRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.previewRenderer.setClearColor( 0x000000, 0 );
        this.previewRenderer.setPixelRatio( window.devicePixelRatio );
        this.previewRenderer.setSize(width, height);

        // Setup camera indicator
        var geometry = new THREE.SphereGeometry( 15, 32, 32 );
        var material = new THREE.MeshBasicMaterial( {color: 0xffff00} );
        var cameraIndicator = new THREE.Mesh( geometry, material );
        this.previewCamera.add(cameraIndicator);

        // Initialize the renderer
        this.previewMount.current.appendChild(this.previewRenderer.domElement);
    }

    /**
     * Tears down the first-person route preview renderer, etc.
     * 
     * @memberof ThreeMap
     */
    destroyPreview () {
        if (this.previewRenderer) {
            this.previewMount.current.removeChild(this.previewRenderer.domElement);
            this.scene.remove(this.previewCamera);
            this.previewCamera = null;
            this.previewRenderer = null;
        }
    }

    /**
     * Makes sure that all non-react operations are halted when the component unmounts
     * 
     * @memberof ThreeMap
     */
    componentWillUnmount () {
        this.stop();
        this.destroyPreview();
        this.mount.current.removeChild(this.renderer.domElement);
        window.removeEventListener ( 'resize', this.onWindowResize, false );
    }

    /**
     * Kicks off the render loop
     * 
     * @memberof ThreeMap
     */
    start () {
        if (!this.frameId) {
            this.frameId = requestAnimationFrame(this.animate);
        }
    }

    /**
     * Repeatedly-called method to handle any frame-by-frame operations
     * 
     * @param {any} ts 
     * @memberof ThreeMap
     */
    animate (ts) {
        // Start stats tracking for frame
        this.stats.begin();

        // Origin animation
        if (this.origin) {
            const scale = Math.cos(ts/500) / 2 + 1;
            this.origin.scale.set(
                scale,
                scale,
                1
            );
        }

        // Routing preview camera - largely from https://github.com/mrdoob/three.js/blob/master/examples/webgl_geometry_extrude_splines.html
        if (this.previewRenderer) {
            // Determine camera location along spline
			var looptime = 20 * 1000;
			var t = ( ts % looptime ) / looptime;
			var pos = this.route.geometry.parameters.path.getPointAt( t );
			pos.multiplyScalar( 1 );

            // interpolation
			var segments = this.route.geometry.tangents.length;
			var pickt = t * segments;
			var pick = Math.floor( pickt );
			var pickNext = ( pick + 1 ) % segments;
			this.binormal.subVectors( this.route.geometry.binormals[ pickNext ], this.route.geometry.binormals[ pick ] );
			this.binormal.multiplyScalar( pickt - pick ).add( this.route.geometry.binormals[ pick ] );
			var dir = this.route.geometry.parameters.path.getTangentAt( t );
			var offset = -15;
			this.normal.copy( this.binormal ).cross( dir );
			// we move on a offset on its binormal
			pos.add( this.normal.clone().multiplyScalar( offset ) );
			this.previewCamera.position.copy( pos );

            const lookAt = this.route.geometry.parameters.path.getPointAt( ( t + 30 / this.route.geometry.parameters.path.getLength() ) % 1 ).multiplyScalar( 1 );
			// camera orientation 2 - up orientation via normal
			lookAt.copy( pos ).add( dir );
            this.normal.z = -1 * this.normal.z;
			this.previewCamera.matrix.lookAt( this.previewCamera.position, lookAt, this.normal );
			this.previewCamera.rotation.setFromRotationMatrix( this.previewCamera.matrix, this.previewCamera.rotation.order );

            this.previewRenderer.render(this.scene, this.previewCamera);
        }

        this.renderer.render(this.scene, this.camera);

        // End stats tracking for frame
        this.stats.end();

        this.frameId = requestAnimationFrame(this.animate);
    }

    /**
     * Handles ending the animation loop
     * 
     * @memberof ThreeMap
     */
    stop () {
        cancelAnimationFrame(this.frameId);
    }

    /**
     * Callback for the window size changing, handling updating the render and camera ratios
     * 
     * @memberof ThreeMap
     */
    onWindowResize () {
        const width = this.mount.current.clientWidth;
        const height = this.mount.current.clientHeight;

        this.renderer.setSize( this.mount.current.clientWidth, this.mount.current.clientHeight );

        // Update the camera
        this.camera.left = width / -2;
        this.camera.right = width / 2;
        this.camera.top = height / 2;
        this.camera.bottom = height / -2;
        this.camera.updateProjectionMatrix();

    }

    /**
     * Helper class to parse an SVG map file for shapes, call the corresponding SVGToMesh method, and add the resulting mesh to a THREE.group
     * 
     * @param {any} svg An SVG document to parse for map shapes
     * @param {object<THREE.group>} group A THREE group which contains all of the map elements
     * @memberof ThreeMap
     */
    parseSVGIntoGroup (svg, group) {
        // TODO: Parse into LoD groups rather than all objects
        const visualElements = SVGService.getSVGVisualElements(svg);

        for (var i = 0; i < visualElements.length; i++) {
            let newShape;
            const svgElement = visualElements[i];

            // Get class styles
            // NOTE: Relies on another instance of svgElement's namespaced class existing in the document
            const otherFill = document.querySelector("." + svgElement.getAttribute("class"));
            let fill = (otherFill ? getComputedStyle( otherFill ).fill : null );
            if (fill === "none") fill = null;

            const otherStroke = document.querySelector("." + svgElement.getAttribute("class"));
            let stroke = (otherStroke ? getComputedStyle( otherStroke ).stroke : null );

            switch (svgElement.tagName) {
                case "circle":
                    newShape = this.SVGToMesh.circle(svgElement, fill, stroke);
                    break;
                case "rect":
                    newShape = this.SVGToMesh.rect(svgElement, fill, stroke);
                    break;
                case "polyline":
                case "polygon":
                    newShape = this.SVGToMesh.polygon(svgElement, fill, stroke);
                    break;
                case "path":
                    newShape = this.SVGToMesh.path(svgElement, fill, stroke);
                    break;
                default:
                    console.warn("ThreeMap::parseSVGIntoGroup : Unknown SVG visual element with tagName " + visualElements[i].tagName + "!");
            }

            if (newShape) {
                group.add( newShape );
            }
        }
    }

    /**
     * Callback for double-tap events
     * Currently set up to set the routing destination as the nearest waypoint to the double-tap
     * 
     * @param {any} ev 
     * @memberof ThreeMap
     */
    handleDoubletap (ev) {
        // Determine 3D position tapped
        const clickedPosition = new THREE.Vector2(
            (ev.srcEvent.clientX / window.innerWidth ) * 2 - 1,
            - ( ev.srcEvent.clientY / window.innerHeight ) * 2 + 1
        );
        
        this.raycaster.setFromCamera(clickedPosition, this.camera);
        const intersect = this.raycaster.intersectObjects(this.mapGroup.children, true)[0];

        if (intersect.uv) {
            // Get nearest waypoint to click position
            const nearestWaypoint = RoutingService.GetWaypointNearestPosition(new Position(intersect.uv.x, intersect.uv.y, 0), this.props.mapName);
            if (nearestWaypoint !== null) {
                this.setState({
                    customRouting: {
                        nearestWaypoint: nearestWaypoint,
                        top: ev.center.y,
                    }
                });
            }
        }
    }
}

ThreeMap.propTypes = {
    mapName: PropTypes.string.isRequired,
    origin: PropTypes.instanceOf(Waypoint),
    destination: PropTypes.instanceOf(Waypoint),
    setOrigin: PropTypes.func.isRequired,
    setDestination: PropTypes.func.isRequired,
    setSelectedPOI: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
}

export default ThreeMap;


// WEBPACK FOOTER //
// ./src/components/ThreeMap.js